import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PROD_HOST = 'https://clearcommunicationapp.com';

/**
 * Mounts once near the app root.
 * Keeps <link rel="canonical"> in sync with the current route so Google
 * Search Console no longer sees "Duplicate without user-selected canonical".
 *
 * Always points to the apex (`clearcommunicationapp.com`), regardless
 * of the current host (preview, apex, www) so all duplicates collapse
 * to a single indexable URL. Cloudflare 308-redirects www→apex, so the
 * canonical signal must match.
 *
 * Guarantees EXACTLY ONE canonical link tag exists at all times — a
 * MutationObserver watches the <head> and prunes any duplicates that
 * react-helmet-async (or future code) injects.
 */
export const useCanonicalUrl = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let normalized = (pathname || '/').split('?')[0].split('#')[0];
    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }
    const canonical = `${PROD_HOST}${normalized}`;

    const pruneAndSet = () => {
      const all = Array.from(document.querySelectorAll('link[rel="canonical"]'));
      // Keep first, remove the rest, then update the kept one's href.
      for (let i = 1; i < all.length; i++) {
        all[i].parentNode && all[i].parentNode.removeChild(all[i]);
      }
      let link = all[0];
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      if (link.getAttribute('href') !== canonical) {
        link.setAttribute('href', canonical);
      }
      // og:url mirror
      let og = document.querySelector('meta[property="og:url"]');
      if (!og) {
        og = document.createElement('meta');
        og.setAttribute('property', 'og:url');
        document.head.appendChild(og);
      }
      if (og.getAttribute('content') !== canonical) {
        og.setAttribute('content', canonical);
      }
    };

    // Run immediately, then again on the next microtask + animation frame
    // so we win against Helmet's deferred render scheduling.
    pruneAndSet();
    Promise.resolve().then(pruneAndSet);
    const raf = requestAnimationFrame(pruneAndSet);

    // Watch for Helmet (or anything else) injecting duplicate canonicals.
    const observer = new MutationObserver(() => {
      const all = document.querySelectorAll('link[rel="canonical"]');
      if (all.length > 1) pruneAndSet();
    });
    observer.observe(document.head, { childList: true, subtree: false });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [pathname]);
};

export default useCanonicalUrl;
